import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import {
  fetchCurrentQuestion,
  fetchExamAccess,
  fetchExamAttempt,
  saveExamAnswer,
  startExamAttempt,
  submitExamAttempt,
} from './examAccessApi';

function questionHtml(question) {
  return { __html: question?.questionText || '' };
}

export default function ExamAccess() {
  const { token = '' } = useParams();
  const [access, setAccess] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [questionState, setQuestionState] = useState(null);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const assignment = access?.assignment;
  const exam = assignment?.examId;
  const member = assignment?.memberId;
  const submitted = attempt?.status === 'submitted' || attempt?.status === 'graded';

  const progressValue = useMemo(() => {
    const total = Number(questionState?.totalQuestions || 0);
    if (!total) return 0;
    return Math.min(100, Math.round((Number(questionState?.answeredCount || 0) / total) * 100));
  }, [questionState]);

  const loadCurrentQuestion = useCallback(async () => {
    const current = await fetchCurrentQuestion(token);
    setQuestionState(current);
    setAnswer('');
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const info = await fetchExamAccess(token);
      setAccess(info);
      try {
        const existingAttempt = await fetchExamAttempt(token);
        setAttempt(existingAttempt);
        if (existingAttempt?.status === 'in_progress') {
          await loadCurrentQuestion();
        }
      } catch (err) {
        if (err.response?.status !== 404) throw err;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'הקישור אינו זמין');
    } finally {
      setLoading(false);
    }
  }, [loadCurrentQuestion, token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStart = async () => {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const started = await startExamAttempt(token);
      setAttempt(started);
      await loadCurrentQuestion();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בפתיחת המבחן');
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!questionState?.question?._id) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await saveExamAnswer(token, {
        questionId: questionState.question._id,
        answerType: 'text',
        value: answer,
        isFinal: true,
      });
      setSuccess('התשובה נשמרה');
      await loadCurrentQuestion();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת התשובה');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const submittedAttempt = await submitExamAttempt(token);
      setAttempt(submittedAttempt);
      setSuccess('המבחן הוגש בהצלחה');
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בהגשת המבחן');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box minHeight="100vh" display="grid" placeItems="center" bgcolor="#f0f4f8">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box minHeight="100vh" bgcolor="#f0f4f8" py={{ xs: 2, md: 5 }} dir="rtl">
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, border: '1px solid #dbe5ef' }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {assignment && (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} mb={2}>
                <Box>
                  <Typography variant="h4" component="h1" fontWeight={800} color="#12395f">
                    {exam?.name || 'מבחן'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    {exam?.subject || ''}{exam?.code ? ` | קוד ${exam.code}` : ''}
                  </Typography>
                </Box>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <Chip label={member ? `${member.firstName || ''} ${member.lastName || ''}`.trim() : 'נבחן'} />
                  <Chip color={submitted ? 'success' : 'primary'} label={submitted ? 'הוגש' : (attempt ? 'בתהליך' : 'טרם התחיל')} />
                </Stack>
              </Stack>

              {exam?.introText && (
                <Alert severity="info" sx={{ mb: 2 }}>{exam.introText}</Alert>
              )}

              <Divider sx={{ my: 2 }} />
            </>
          )}

          {!attempt && assignment && (
            <Stack spacing={2}>
              <Typography>
                המבחן מוכן לפתיחה. לאחר הפתיחה ניתן לשמור תשובות ולהגיש בסיום.
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowOutlinedIcon />}
                  onClick={handleStart}
                  disabled={busy}
                >
                  פתח מבחן
                </Button>
              </Box>
            </Stack>
          )}

          {attempt && !submitted && (
            <Stack spacing={2.5}>
              <Box>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    נשמרו {questionState?.answeredCount || 0} מתוך {questionState?.totalQuestions || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progressValue}%
                  </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={progressValue} />
              </Box>

              {questionState?.question ? (
                <>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff' }}>
                    <Typography variant="overline" color="text.secondary">
                      שאלה
                    </Typography>
                    <Typography
                      component="div"
                      sx={{ mt: 1, lineHeight: 1.9, fontSize: 18 }}
                      dangerouslySetInnerHTML={questionHtml(questionState.question)}
                    />
                  </Paper>

                  <TextField
                    label="תשובה"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    multiline
                    minRows={6}
                    fullWidth
                    disabled={busy}
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                    <Button
                      variant="contained"
                      startIcon={<SaveOutlinedIcon />}
                      onClick={handleSave}
                      disabled={busy || !answer.trim()}
                    >
                      שמור והמשך
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<SendOutlinedIcon />}
                      onClick={handleSubmit}
                      disabled={busy}
                    >
                      הגש מבחן
                    </Button>
                  </Stack>
                </>
              ) : (
                <Stack spacing={2} alignItems="flex-start">
                  <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
                    כל השאלות נשמרו. אפשר להגיש את המבחן.
                  </Alert>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SendOutlinedIcon />}
                    onClick={handleSubmit}
                    disabled={busy}
                  >
                    הגש מבחן
                  </Button>
                </Stack>
              )}
            </Stack>
          )}

          {submitted && (
            <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
              המבחן הוגש ונקלט במערכת.
            </Alert>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
